import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { CoveragePetList } from './CoveragePetList';
import { IneligiblePetsList } from './IneligiblePetsList';
import { RemovedPetsList } from './RemovedPetsList';

import { PET_DEMOS_SPECIES } from '../../constants';

import styles from './CoveragePetsSection.module.css';

const CoveragePetsSection = ({
  allPets = [],
  ineligiblePets = [],
  ineligiblePetsCounts = {},
  initialCoveragePets = [],
  isCompleted = false,
  numberOfCats = 0,
  numberOfDogs = 0,
  numberOfPets = 0,
  planName = '',
  previousEligiblePets = [],
  removedPets = [],
  removedPetsCounts = {},
}) => {
  const { t } = useTranslation('petDemos');

  const renderIneligiblePetDescription = () => {
    if (isCompleted) {
      if (ineligiblePets && ineligiblePets.length > 0) {
        return t('policyInformation.ineligiblePetsAge', { planName });
      }

      return t('policyInformation.ineligiblePetsNone');
    }

    return t('policyInformation.petsIneligibleDescription');
  };

  const fromPrevious = previousEligiblePets?.length > 0
    ? previousEligiblePets.map((pet) => ({
      demographicData: {
        isIneligible: false,
        isRemoved: false,
        isRollover: true,
        petName: pet?.demographicData?.petName ?? '',
        species: pet?.demographicData?.species,
      },
      employeeDependentId: pet?.employeeDependentId,
    }))
    : [];

  const periodPets = initialCoveragePets || [];

  const createEmptyRolloverSlot = ({ employeeDependentId, species }) => ({
    demographicData: {
      isIneligible: false,
      isRemoved: false,
      isRollover: true,
      petName: '',
      species,
    },
    employeeDependentId,
  });

  const extraSlots = useMemo(() => {
    if (periodPets.length <= fromPrevious.length) {
      return [];
    }

    const fromPreviousIds = new Set(
      fromPrevious.map((prev) => prev.employeeDependentId),
    );

    const dogsInPrevious = fromPrevious.filter(
      (pet) => pet.demographicData?.species === PET_DEMOS_SPECIES.dog,
    ).length;

    const catsInPrevious = fromPrevious.filter(
      (pet) => pet.demographicData?.species === PET_DEMOS_SPECIES.cat,
    ).length;

    const missingDogs = Math.max(0, numberOfDogs - dogsInPrevious);
    const missingCats = Math.max(0, numberOfCats - catsInPrevious);

    const dogsNotInPrevious = periodPets.filter(
      (pet) => pet.demographicData?.species === PET_DEMOS_SPECIES.dog
        && !fromPreviousIds.has(pet.employeeDependentId),
    );
    const catsNotInPrevious = periodPets.filter(
      (pet) => pet.demographicData?.species === PET_DEMOS_SPECIES.cat
        && !fromPreviousIds.has(pet.employeeDependentId),
    );

    const slots = [];

    dogsNotInPrevious.slice(0, missingDogs).forEach((pet) => {
      slots.push(createEmptyRolloverSlot({
        employeeDependentId: pet.employeeDependentId,
        species: PET_DEMOS_SPECIES.dog,
      }));
    });

    catsNotInPrevious.slice(0, missingCats).forEach((pet) => {
      slots.push(createEmptyRolloverSlot({
        employeeDependentId: pet.employeeDependentId,
        species: PET_DEMOS_SPECIES.cat,
      }));
    });

    return slots;
  }, [
    fromPrevious,
    numberOfCats,
    numberOfDogs,
    periodPets,
  ]);

  const initialItems = fromPrevious.length > 0
    ? [...fromPrevious, ...extraSlots]
    : periodPets.map((pet) => ({
      demographicData: {
        isIneligible: false,
        isRemoved: false,
        isRollover: false,
        petName: pet?.demographicData?.petName || '',
        species: pet?.demographicData?.species,
      },
      employeeDependentId: pet?.employeeDependentId,
    }));

  return (
    <div className={styles.policyActionCardPetsSection}>
      <div className={styles.policyActionCardPetsSummary}>
        <div className={styles.policyActionCardPetsSummaryHeader}>
          <h4 className={styles.policyActionCardPetsTotal}>
            {t('policyInformation.petsTotal', {
              total: numberOfPets,
            })}
          </h4>

          <span className={styles.policyActionCardPets}>
            {t('policyInformation.dogsAndCats', {
              cats: numberOfCats,
              dogs: numberOfDogs,
            })}
          </span>
        </div>

        <CoveragePetList
          editedItems={allPets}
          initialItems={initialItems}
        />
      </div>

      <div className={styles.policyActionCardPetsStatusContainer}>
        <div className={styles.policyActionCardPetsStatusRow}>
          <div className={styles.policyActionCardPetsStatusRowContent}>
            <span className={styles.policyPetsStatusLabel}>
              {t('policyInformation.petsRemovedLabel')}
            </span>

            <RemovedPetsList
              removedPets={removedPets}
              removedPetsCounts={removedPetsCounts}
            />
          </div>
        </div>

        <div className={styles.policyActionCardPetsStatusRow}>
          <div className={styles.policyActionCardPetsStatusRowContent}>
            <span className={styles.policyPetsStatusLabel}>
              {t('policyInformation.petsIneligibleLabel')}
            </span>

            <IneligiblePetsList
              ineligiblePets={ineligiblePets}
              ineligiblePetsCounts={ineligiblePetsCounts}
              isCompleted={isCompleted}
            />
          </div>
        </div>

        <p className={styles.petsIneligibleDescription}>
          {renderIneligiblePetDescription()}
        </p>
      </div>
    </div>
  );
};

export { CoveragePetsSection };
